import { useEffect, useMemo, useRef, useState } from "react";

import type { DateValue } from "@mantine/dates";

import { isValid, parseISO } from "date-fns";

/** Selected questionnaire date field value from Mantine form state. */
export type QuestionnaireDateFormValue = Date | string | null | undefined;

function toValueTimestamp(formValue: QuestionnaireDateFormValue): number | null {
  if (!formValue) return null;

  if (formValue instanceof Date) {
    return Number.isNaN(formValue.getTime()) ? null : formValue.getTime();
  }

  const parsed = parseISO(formValue);
  return isValid(parsed) ? parsed.getTime() : null;
}

/**
 * Normalizes Mantine calendar navigation values (`YYYY-MM-DD` string or `Date`).
 */
function parseCalendarNavigationDate(nextDate: DateValue): Date | undefined {
  if (nextDate == null) return undefined;

  if (nextDate instanceof Date) {
    return Number.isNaN(nextDate.getTime()) ? undefined : nextDate;
  }

  const parsed = parseISO(nextDate);
  return isValid(parsed) ? parsed : undefined;
}

/**
 * Keeps Mantine DateInput calendar navigation stable when unrelated form updates
 * re-render with new Date object references for an unchanged selected value.
 */
export function useStableCalendarDateInputState(formValue: QuestionnaireDateFormValue) {
  const valueTimestamp = toValueTimestamp(formValue);
  const stableDateValueRef = useRef<Date | null>(null);

  const stableDateValue = useMemo(() => {
    if (valueTimestamp == null) {
      stableDateValueRef.current = null;
      return null;
    }

    if (stableDateValueRef.current?.getTime() === valueTimestamp) {
      return stableDateValueRef.current;
    }

    const nextDate = new Date(valueTimestamp);
    stableDateValueRef.current = nextDate;
    return nextDate;
  }, [valueTimestamp]);

  const [calendarDate, setCalendarDate] = useState<Date | undefined>(
    () => stableDateValue ?? undefined,
  );

  useEffect(() => {
    if (valueTimestamp != null) {
      setCalendarDate(new Date(valueTimestamp));
    }
  }, [valueTimestamp]);

  const handleCalendarDateChange = (nextDate: DateValue) => {
    const parsedDate = parseCalendarNavigationDate(nextDate);
    if (parsedDate) {
      setCalendarDate(parsedDate);
    }
  };

  return {
    stableDateValue,
    calendarDate,
    handleCalendarDateChange,
  };
}
